import React from 'react';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { ArrowLeft } from '@/components/Icons/ArrowLeft';
import { useHasFamilySubscription } from '@/hooks/useHasFamilySubscription';
import { useScrollFadeProgress } from '@/hooks/useScrollFadeProgress';
import { JournalDetails } from '@/pages/Overview/JournalDetails';
import { JournalMobilePaywall } from '@/pages/Overview/JournalMobilePaywall';
import { PeriodPicker } from '@/pages/Overview/PeriodPicker';
import type { Journal } from '@/types/journal';
import type { StudyYearPeriod } from '@/types/overview';
import { getTeacherNames } from '@/utils/journal';

interface Props {
  journal: Journal;
  periods: StudyYearPeriod[];
  selectedPeriod: StudyYearPeriod;
  onPeriodChange: (period: StudyYearPeriod) => void;
  onBack: () => void;
}

export const JournalMobileDetails: React.FC<Props> = ({
  journal,
  periods,
  selectedPeriod,
  onPeriodChange,
  onBack,
}) => {
  const hasSubscription = useHasFamilySubscription();
  const { progress, handleScroll } = useScrollFadeProgress();

  return (
    <div className="flex min-h-0 flex-1 animate-zoom-in flex-col">
      <div className="relative flex shrink-0 items-center justify-between gap-3 px-4 pt-[calc(12px+env(safe-area-inset-top))] pb-3">
        <button
          type="button"
          onClick={onBack}
          className="flex size-6 shrink-0 cursor-pointer items-center justify-center"
        >
          <ArrowLeft className="size-6 text-foreground" />
        </button>

        <h2
          style={{ opacity: progress }}
          className="text-callout-16 absolute left-1/2 max-w-[55%] -translate-x-1/2 truncate text-foreground"
        >
          {journal.name}
        </h2>

        <PeriodPicker
          isAlignedRight
          periods={periods}
          value={selectedPeriod}
          onChange={onPeriodChange}
        />
      </div>

      <AutoHideScrollArea
        hasTopShadow
        className="flex min-h-0 flex-1 flex-col"
        viewportClassName="[&>div]:h-full"
        onScroll={handleScroll}
      >
        <div className="flex min-h-full flex-col gap-6 px-4 pb-[calc(24px+env(safe-area-inset-bottom))]">
          <div className="flex min-w-0 flex-col gap-3 pt-1">
            {hasSubscription && (
              <h1 className="text-title-36 truncate text-foreground">
                {journal.name}
              </h1>
            )}
            <p className="text-body-14 truncate text-subtle-foreground">
              {getTeacherNames(journal)}
            </p>
          </div>

          {hasSubscription && (
            <JournalDetails
              journalId={journal.journalId}
              period={selectedPeriod}
            />
          )}
          {!hasSubscription && <JournalMobilePaywall />}
        </div>
      </AutoHideScrollArea>
    </div>
  );
};
